import React, { useState, useRef } from 'react';

function formatSize(bytes) {
    return bytes < 1024 * 1024
        ? `${(bytes / 1024).toFixed(0)} Ko`
        : `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * FileDropzone — composant de dépôt de fichier réutilisable.
 *
 * Props :
 *   accept      string[]       Extensions acceptées, ex. ['.xls', '.xlsx', '.csv']
 *   maxSizeMb   number         Taille maximale en Mo (défaut : 10)
 *   hint        string         Texte d'aide affiché dans la zone (optionnel, généré automatiquement sinon)
 *   onFile      (File) => void Appelé quand un fichier valide est sélectionné
 *   file        File|null      Fichier courant (contrôlé par le parent)
 *   uploading   bool           Upload S3 en cours
 *   uploadDone  bool           Upload S3 terminé avec succès
 *   uploadError string|null   Message d'erreur d'upload (depuis le parent)
 */
function FileDropzone({
    accept = [],
    maxSizeMb = 10,
    hint,
    onFile,
    file = null,
    uploading = false,
    uploadDone = false,
    uploadError = null,
}) {
    const [dragOver, setDragOver] = useState(false);
    const [fileError, setFileError] = useState(null);
    const inputRef = useRef(null);

    const maxSize = maxSizeMb * 1024 * 1024;
    const acceptAttr = accept.join(',');
    const defaultHint = hint ?? `Formats acceptés : ${accept.join(', ')} — ${maxSizeMb} Mo max`;

    const validate = (selectedFile) => {
        const ext = selectedFile.name.slice(selectedFile.name.lastIndexOf('.')).toLowerCase();
        if (accept.length > 0 && !accept.includes(ext)) {
            setFileError(`Format non supporté. Utilisez un fichier ${accept.join(', ')}.`);
            return false;
        }
        if (selectedFile.size > maxSize) {
            setFileError(`Le fichier dépasse la taille maximale de ${maxSizeMb} Mo.`);
            return false;
        }
        setFileError(null);
        return true;
    };

    const handlePick = (selectedFile) => {
        if (!selectedFile || uploading) return;
        if (validate(selectedFile)) onFile(selectedFile);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handlePick(e.dataTransfer.files[0]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!uploading) setDragOver(true);
    };

    const handleInputChange = (e) => {
        handlePick(e.target.files[0]);
        e.target.value = '';
    };

    const errorMessage = fileError || uploadError;
    const hasError = !!errorMessage;

    const dropzoneClass = [
        'dropzone',
        uploading              ? 'dropzone--uploading' : '',
        dragOver && !uploading ? 'dropzone--active'    : '',
        uploadDone             ? 'dropzone--filled'    : '',
        hasError               ? 'dropzone--error'     : '',
    ].filter(Boolean).join(' ');

    const renderContent = () => {
        if (uploading) {
            return (
                <div className="dropzone-state">
                    <i className="fas fa-spinner fa-spin dropzone-state-icon dropzone-state-icon--uploading" />
                    <span className="dropzone-state-name">{file?.name}</span>
                    <span className="dropzone-state-hint">Upload en cours…</span>
                </div>
            );
        }
        if (uploadDone) {
            return (
                <div className="dropzone-state">
                    <i className="fas fa-check-circle dropzone-state-icon dropzone-state-icon--success" />
                    <span className="dropzone-state-name">{file?.name}</span>
                    <span className="dropzone-state-hint">{file ? formatSize(file.size) : ''}</span>
                    <span className="dropzone-state-change">Cliquer pour changer</span>
                </div>
            );
        }
        if (hasError) {
            return (
                <div className="dropzone-state">
                    <i className="fas fa-times-circle dropzone-state-icon dropzone-state-icon--error" />
                    <span className="dropzone-state-name">{errorMessage}</span>
                    <span className="dropzone-state-hint">Cliquer pour réessayer</span>
                </div>
            );
        }
        return (
            <>
                <i className="fas fa-cloud-upload-alt dropzone-idle-icon" />
                <p className="dropzone-idle-title">
                    Glissez votre fichier ici ou{' '}
                    <span className="dropzone-cta">parcourez</span>
                </p>
                <p className="dropzone-idle-hint">{defaultHint}</p>
            </>
        );
    };

    return (
        <div
            className={dropzoneClass}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setDragOver(false)}
            onClick={() => !uploading && inputRef.current?.click()}
        >
            <input
                ref={inputRef}
                type="file"
                accept={acceptAttr}
                style={{ display: 'none' }}
                onChange={handleInputChange}
            />
            {renderContent()}
        </div>
    );
}

export { FileDropzone };
